import { create } from "zustand";

import type { EmissionProtocol } from "@/lib/protocol";

/**
 * Explicit report-type id -> reporting-framework map (plan 6.4).
 *
 * Ids come from `moduletypes` and are mirrored by
 * `EmissionConstants.REPORT_FRAMEWORK_ISO` / `_GHG` on the server, which is the
 * authority. This map exists so the browser can mirror the same taxonomy
 * decision instead of inventing one.
 *
 * Note `10662` is also the GHG framework *report* sub-type (moduletypes `GHG Protocol`,
 * group 25), the same way `460` is the ISO report sub-type. Both ids are read from
 * `companypreferences.reports.framework`, so the client must keep the mapping even where the
 * row is only present in the database and not in the seed script. See plan 6.3.
 */
export const FRAMEWORK_BY_REPORT_TYPE: Readonly<
  Record<number, EmissionProtocol>
> = Object.freeze({
  460: "iso",
  10662: "ghg",
});

/** Reverse lookup, for labelling a report type the server already stored. */
export const REPORT_TYPE_BY_FRAMEWORK: Readonly<
  Record<EmissionProtocol, number>
> = Object.freeze({
  iso: 460,
  ghg: 10662,
});

export type FrameworkSource = "server" | "pre-hydration" | "unresolved";

interface FrameworkState {
  framework: EmissionProtocol;
  /** Report-type id the server resolved, when it was a known id. */
  reportTypeId: number | null;
  /** True when the stored framework id is not in the map above. */
  unknownReportTypeId: number | null;
  source: FrameworkSource;
  hydrate: (framework: EmissionProtocol, reportTypeId?: number | null) => void;
  /** Records that the id could not be mapped; surfaces rather than defaulting. */
  markUnresolved: (reportTypeId: number | null) => void;
  preHydrate: (framework: EmissionProtocol) => void;
  reset: () => void;
}

/**
 * Reads the pre-hydration value written by older builds. It is only ever used
 * until the server's `companypreferences.reports.framework` arrives; once that
 * lands it always wins (plan 6.2), because a stale browser value is what files
 * emissions against the wrong taxonomy.
 */
function readLegacyProtocol(): EmissionProtocol | null {
  try {
    const stored = window.localStorage.getItem("protocol");
    return stored === "ghg" || stored === "iso" ? stored : null;
  } catch {
    return null;
  }
}

export const useFrameworkStore = create<FrameworkState>()((set) => ({
  framework: readLegacyProtocol() ?? "iso",
  reportTypeId: null,
  unknownReportTypeId: null,
  source: readLegacyProtocol() ? "pre-hydration" : "unresolved",

  hydrate: (framework, reportTypeId) =>
    set({
      framework,
      reportTypeId: reportTypeId ?? REPORT_TYPE_BY_FRAMEWORK[framework],
      unknownReportTypeId: null,
      source: "server",
    }),

  markUnresolved: (reportTypeId) =>
    set({ unknownReportTypeId: reportTypeId, source: "unresolved" }),

  preHydrate: (framework) => set({ framework, source: "pre-hydration" }),

  reset: () =>
    set({
      framework: readLegacyProtocol() ?? "iso",
      reportTypeId: null,
      unknownReportTypeId: null,
      source: "unresolved",
    }),
}));

/**
 * Resolves `companypreferences.reports.framework` into a framework.
 *
 * Returns `null` when the tenant has no framework configured or when the stored
 * id is not one we recognise. Callers must treat `null` as "ask the user" rather
 * than falling back to ISO: silently defaulting is how a wrongly-grouped
 * inventory happens.
 */
export function resolveFrameworkFromIds(
  frameworkIds: readonly number[] | undefined,
): { framework: EmissionProtocol; reportTypeId: number } | null {
  if (!frameworkIds || frameworkIds.length === 0) return null;
  for (const raw of frameworkIds) {
    const id = Number(raw);
    const mapped = FRAMEWORK_BY_REPORT_TYPE[id];
    if (mapped) return { framework: mapped, reportTypeId: id };
  }
  return null;
}
